/**
 * Route definitions for all admin pages with lazy loading
 * and protected route wrappers.
 *
 * Each route uses the same fine-grained permission as its
 * corresponding AdminSidebar nav item, matching the backend
 * routePermissions registry in authz.go.
 *
 * Feature-gate migration (TASK-FT-MIGRATE-PLATFORM-ADMIN): the local
 * fail-OPEN `withVisibility(...)` wrapper and the `isFeatureVisible` import from
 * `config/featureVisibility` are removed. Release-toggle gating now flows through
 * the shared fail-CLOSED gate (`web/src/feature-gate`): a disabled feature has
 * its `<Route>` OMITTED from the router (via `buildGatedRoutes`), not
 * rendered-then-redirected, so a direct URL to it falls through to the shared
 * `NotFoundPage` (SPEC-FEATURE-TOGGLES-RULE-005). RBAC (`withProtection` /
 * ProtectedRoute / PermissionGate) is ORTHOGONAL to the release toggle and is
 * preserved on every route — the toggle never replaces it
 * (SPEC-FEATURE-TOGGLES-RULE-003/005). Legacy colon keys (`admin:system`, …) map
 * to canonical dot keys (`admin.system`, …) per the canonical registry; routes
 * that were unconditionally visible under the legacy fail-open config carry no
 * `feature` key and stay permanently on (no regression).
 *
 * @module apps/admin/routes
 * @see TASK-HLD-PERMISSION-ROUTES
 * @see TASK-FT-MIGRATE-PLATFORM-ADMIN
 * @see ARCH-005-MOD-005
 * @see SPEC-FEATURE-TOGGLES
 */
import React from "react";
import type { RouteObject } from "react-router-dom";
import { ProtectedRoute } from "../../components/shared/ProtectedRoute";
import { RouteErrorBoundary } from "../../components/shared/RouteErrorBoundary";
import {
  buildGatedRoutes,
  NotFoundPage,
  type GatedRouteDescriptor,
  type LazyLoader,
} from "../../feature-gate";

// ---------------------------------------------------------------------------
// Wrapper helpers
// ---------------------------------------------------------------------------

/**
 * Wrap a lazy page module so that RBAC (`<ProtectedRoute>`) and the route error
 * boundary travel INSIDE the code-split chunk. `buildGatedRoutes` lazy-loads the
 * returned module and wraps it in `<Suspense>`, so the page (and its protection)
 * are only fetched once the release toggle keeps the route. The release toggle
 * (route omission) and RBAC (`requiredPermission`) compose: a kept route still
 * enforces its permission.
 */
function protectedLoader(
  importFn: () => Promise<{ default: React.ComponentType }>,
  requiredPermission?: string,
): LazyLoader {
  return () =>
    importFn().then((mod) => {
      const Page = mod.default;
      const Protected: React.ComponentType<unknown> = () => (
        <RouteErrorBoundary>
          <ProtectedRoute requiredPermission={requiredPermission}>
            <Page />
          </ProtectedRoute>
        </RouteErrorBoundary>
      );
      return { default: Protected };
    });
}

// ---------------------------------------------------------------------------
// Gated route descriptors
// ---------------------------------------------------------------------------
//
// `feature` carries the CANONICAL dot-key when a route is release-gateable. Only
// `admin.tenants.presets` is a managed admin release toggle today, and it gates
// the Presets tab/catalogue (inside TenantDetailPage / PluginPresetCatalogue),
// not a top-level route — so every admin ROUTE is unconditionally on (no
// `feature`), preserving the legacy fail-open visibility with no regression. A
// future flagged-off route adds its `feature` key here AND in the canonical
// registry + config source.

const descriptors: GatedRouteDescriptor[] = [
  // -- Overview --
  {
    index: true,
    loader: protectedLoader(() => import("../../pages/admin/DashboardPage"), "dashboard.read"),
  },
  {
    path: "system",
    loader: protectedLoader(() => import("../../pages/admin/SystemResourcesPage"), "system.read"),
  },
  {
    path: "operations",
    loader: protectedLoader(() => import("../../pages/admin/OperationsPage"), "system.read"),
  },
  {
    // `platform.admin`, not `system.read` (D-RLY-34; ADR
    // inference-relay-and-local-engines §11.5 "fix the tenant.manage vs
    // system.read mismatch"): the backend gates every Inference Health route
    // on `tenant.manage` plus the platform-admin check, and `tenant.manage` is
    // not in the frontend vocabulary (authz.go FrontendPermissions), so the
    // string only platform_admin holds there is the matching gate.
    path: "inference-health",
    loader: protectedLoader(() => import("../../pages/admin/InferenceHealthPage"), "platform.admin"),
  },
  {
    // The cost ledger (D-CST-01 A): gated on the literal `costs.view`, which
    // /me/permissions passes through from the PDP resolver (platform_admin by
    // default, a per-user revoke honoured; authz.go PermCostsView - no
    // FrontendPermissions entry, the documents.ocr.review precedent). A revoke
    // hides the page only: while the AUTHZ_PDP_* route lists are empty, the
    // gateway still decides these routes on the token's realm roles, so it
    // does not refuse a revoked admin (35's review 2026-11-04, authz lens).
    // Pinned by CostsRoute.test.tsx. Release key admin.costs (SPEC-FEATURE-TOGGLES-RULE-008), the
    // same key as the sidebar item; ON in every environment, as the other
    // admin pages (D-RLY-59).
    path: "costs",
    feature: "admin.costs",
    loader: protectedLoader(() => import("../../pages/admin/costs/CostsPage"), "costs.view"),
  },
  {
    // Admin storage dashboard. Protected by admin role gate at router level.
    // Do NOT use "admin.storage.read" — phantom permission (Permission Vocabulary §4).
    path: "storage",
    loader: protectedLoader(() => import("../../pages/admin/StoragePage")),
  },
  {
    path: "growth-trends",
    loader: protectedLoader(() => import("../../pages/admin/GrowthTrendsPage"), "dashboard.read"),
  },
  {
    path: "details",
    loader: protectedLoader(() => import("../../pages/admin/DetailsPage"), "dashboard.read"),
  },
  {
    path: "knowledge-libraries",
    feature: "admin.knowledge-libraries",
    loader: protectedLoader(() => import("../../pages/admin/KnowledgeLibrariesPage"), "platform.admin"),
  },
  // -- Management --
  {
    path: "tenants",
    loader: protectedLoader(() => import("../../pages/admin/TenantsPage"), "tenant.read"),
  },
  {
    path: "tenants/:id",
    loader: protectedLoader(() => import("../../pages/admin/TenantDetailPage"), "tenant.read"),
  },
  {
    // Surface templates (SPEC-TENANT-SURFACES UI-002, TASK-TSURF-W5-TEMPLATES-PAGE).
    // Release-gated behind the SAME key as the tenant Surfaces tab
    // (`admin.tenants.surfaces`, fail-closed OFF in the base config): the page
    // and the tab are two halves of one surface-governance feature and are
    // released together.
    //
    // `tenant.read`, NOT `tenant.manage`, and deliberately so. The endpoints
    // this page calls all require `tenant.manage` (API-005..009), but that
    // string is NOT in the frontend permission vocabulary: FrontendPermissions
    // in internal/api/middleware/authz.go (~L1611) serves `tenant.read` to
    // platform_admin and never `tenant.manage`, and `hasPermission` is an exact
    // set membership test with no wildcard (web/src/hooks/usePermissions.ts
    // ~L116). A loader asking for `tenant.manage` would therefore redirect
    // EVERY real platform admin away from the page - the same trap the
    // `plugin.config.govern` note in this file records. `tenant.read` is the
    // gate its sibling TenantDetailPage already uses; the real authorization is
    // the gateway's, and a caller who lacks `tenant.manage` gets the typed 403
    // the page renders as its auth-failure state rather than a blank redirect.
    path: "surface-templates",
    feature: "admin.tenants.surfaces",
    loader: protectedLoader(
      () => import("../../pages/admin/SurfaceTemplatesPage"),
      "tenant.read",
    ),
  },
  {
    path: "workspaces",
    loader: protectedLoader(() => import("../../pages/admin/WorkspacesPage"), "workspace.read"),
  },
  {
    path: "workspaces/:workspaceId",
    loader: protectedLoader(() => import("../../pages/admin/WorkspaceDetailPage"), "workspace.read"),
  },
  {
    path: "users",
    loader: protectedLoader(() => import("../../pages/admin/UsersPage"), "member.read"),
  },
  {
    path: "user-search",
    loader: protectedLoader(() => import("../../pages/admin/UserSearchPage"), "member.read"),
  },
  {
    path: "agents",
    loader: protectedLoader(() => import("../../pages/admin/AgentsPage"), "agent.read"),
  },
  {
    path: "agents/:id",
    loader: protectedLoader(() => import("../../pages/admin/AgentDetailPage"), "agent.read"),
  },
  {
    path: "registrations",
    loader: protectedLoader(() => import("../../pages/admin/RegistrationsPage"), "registration.read"),
  },
  {
    path: "invitations",
    loader: protectedLoader(() => import("../../pages/admin/InvitationsPage"), "member.read"),
  },
  // -- Configuration --
  {
    path: "providers",
    loader: protectedLoader(() => import("../../pages/admin/ProvidersPage"), "provider.read"),
  },
  {
    path: "models",
    loader: protectedLoader(() => import("../../pages/admin/ModelsPage"), "model.read"),
  },
  {
    // LLM models (ADR inference-relay-and-local-engines §10.7, §11.5; slice
    // S5b): the signed catalog, the internal engines and their switch history.
    // Viewing needs `model.read` (§10.7; D-RLY-41 item 2), which in the
    // frontend vocabulary only platform_admin holds (authz.go
    // FrontendPermissions), the audience the backend's platform-admin check
    // serves; the page shows its Switch action only to `platform.admin`, and
    // the backend refuses a switch from anyone else.
    // Release key admin.llm-models (SPEC-FEATURE-TOGGLES-RULE-008), the same
    // key as the sidebar item and the backend llm-models / llm-engines routes;
    // ON everywhere by CEO ruling 2026-09-29 (D-RLY-59).
    path: "llm-models",
    feature: "admin.llm-models",
    loader: protectedLoader(() => import("../../pages/admin/LlmModelsPage"), "model.read"),
  },
  {
    // Relay agents (ADR inference-relay-and-local-engines §9.1, §11.5; slice
    // S5): created and edited only by a platform admin (OD-1). The backend
    // gates /admin/relay-agents on the platform-admin check with
    // denyAPIKeyFunc, so the route gates on `platform.admin`, the frontend
    // string only platform_admin holds (the D-RLY-34 pattern).
    // Release key admin.relay-agents (SPEC-FEATURE-TOGGLES-RULE-008), the
    // same key as the detail route below, the sidebar item and the backend
    // relay-agents routes; ON everywhere by CEO ruling 2026-09-29 (D-RLY-59).
    path: "relay-agents",
    feature: "admin.relay-agents",
    loader: protectedLoader(() => import("../../pages/admin/RelayAgentsPage"), "platform.admin"),
  },
  {
    path: "relay-agents/:id",
    feature: "admin.relay-agents",
    loader: protectedLoader(
      () => import("../../pages/admin/relay-agents/RelayAgentDetailPage"),
      "platform.admin",
    ),
  },
  {
    path: "feature-flags",
    loader: protectedLoader(() => import("../../pages/admin/FeatureFlagsPage"), "feature_flag.read"),
  },
  {
    path: "plugins",
    loader: protectedLoader(() => import("../../pages/admin/PluginListPage"), "settings.read"),
  },
  {
    // Platform crawler CATALOGUE config (ARCH-005-MOD-012 / TASK-CF-PLATFORM-CONFIG-FE):
    // set tenant_selectable + default config_overrides per approved crawler.
    // Release-gated: the crawler framework is in flight and released per
    // environment only (dev/staging ON via VERISA_FEATURE_OVERRIDES; prod and
    // gov pinned OFF — product decision 2026-07-14, EPIC-CRAWLER-FRAMEWORK).
    path: "crawler-config",
    feature: "admin.crawlers",
    loader: protectedLoader(() => import("../../pages/admin/CrawlerConfigPage"), "settings.read"),
  },
  {
    // Always-on (legacy featureVisibility "admin:plugin-approvals" was true); no release toggle.
    path: "plugin-approvals",
    loader: protectedLoader(() => import("../../pages/admin/PluginApprovalsPage"), "settings.read"),
  },
  {
    // Per-tenant plugin config-governance (ARCH-005-MOD-014, SPEC-PLUGIN-ARCH-API-009).
    // Always-on (legacy featureVisibility "admin:plugin-config-governance" was true).
    path: "plugin-config-governance",
    loader: protectedLoader(() => import("../../pages/admin/PluginConfigGovernancePage"), "plugin.config.govern"),
  },
  {
    // Agentic research platform policy (docs/architecture/agentic-research.md;
    // D-AGR-01..04). Release-gated fail-closed: the plugin ships dark until an
    // environment releases admin.agentic-research (SPEC-FEATURE-TOGGLES-RULE-008).
    path: "agentic-research",
    feature: "admin.agentic-research",
    loader: protectedLoader(() => import("../../plugins/agentic-research/ResearchPolicyPage"), "plugin.config.govern"),
  },
  {
    // Translator per-tenant models, prices and usage export (PR #2368 review
    // F8, DECISIONS.md D-TRL-01). Release-gated fail-closed by the plugin's one
    // key, plugin.translator, which also gates its API prefixes (feature_gate.go).
    path: "translator",
    feature: "plugin.translator",
    loader: protectedLoader(() => import("../../plugins/translator/TranslatorPolicyPage"), "plugin.config.govern"),
  },
  // -- Tools --
  {
    path: "api-keys",
    loader: protectedLoader(() => import("../../pages/admin/ApiKeysPage"), "api_key.read"),
  },
  {
    path: "chat-logs",
    loader: protectedLoader(() => import("../../pages/admin/ChatLogsPage"), "chat_log.read"),
  },
  {
    path: "audit-logs",
    loader: protectedLoader(() => import("../../pages/admin/AuditLogsPage"), "audit.read"),
  },
  {
    // NO requiredPermission, deliberately. The Documents/SBOM/Status tabs are
    // READ tier - `open_to_authenticated`, permitted to any authenticated
    // principal per SPEC-COMPLIANCE-DELIVERY RULE-003 - so no single frontend
    // permission gates the whole page; the admin-tier tabs are gated per-tab on
    // compliance.admin inside the page, not per-route. Mirrors the paramless
    // StoragePage call.
    //
    // Release-gated: this is a DARK SLICE. Documents, SBOM, Status,
    // Vulnerabilities and Advisories are delivered; AI monitoring and
    // Incidents have no backend yet and render a typed "not yet available"
    // state. Fail-closed OFF in the base config and
    // released per environment via VERISA_FEATURE_OVERRIDES (SPEC-FEATURE-
    // TOGGLES RULE-005/008). Releasing it to a customer-facing environment
    // also publishes the delivered document set, which is still marked
    // VAZLAT (draft) - an owner decision, not an implementation default.
    path: "compliance",
    feature: "admin.compliance",
    loader: protectedLoader(() => import("../../pages/admin/CompliancePage")),
  },
  {
    path: "permission-audit",
    loader: protectedLoader(() => import("../../pages/admin/PermissionAuditPage"), "audit.read"),
  },
  {
    path: "experiments",
    loader: protectedLoader(() => import("../../pages/admin/ABTestingPage"), "experiment.read"),
  },
  {
    path: "playground",
    loader: protectedLoader(() => import("../../pages/admin/ChatPlaygroundPage"), "playground.read"),
  },
  // -- System --
  {
    path: "settings",
    loader: protectedLoader(() => import("../../pages/admin/SystemSettingsPage"), "settings.read"),
  },
  {
    path: "debug",
    loader: protectedLoader(() => import("../../pages/admin/DebugPage"), "dashboard.read"),
  },
  // Agent config pages live in the tenant app (manage.verisa.ai)
];

// ---------------------------------------------------------------------------
// Route definitions
// ---------------------------------------------------------------------------
//
// `buildGatedRoutes` OMITS any descriptor whose `feature` key is not in the
// server-resolved enabled set and code-splits every surviving route. A terminal
// catch-all renders the shared NotFoundPage so an unknown admin path (or a route
// omitted by a disabled toggle) lands on a genuine 404 instead of being silently
// redirected (SPEC-FEATURE-TOGGLES-RULE-005).

// Built at RENDER time (inside <ReleaseFeaturesGate>, after the release-features
// bootstrap resolves) — NOT at module import, when the enabled set is still empty.
// Building here means a route gated `true` in config actually surfaces once
// released (SPEC-FEATURE-TOGGLES-RULE-007), instead of being frozen-omitted.
export function buildAdminRoutes(): RouteObject[] {
  return [
    ...buildGatedRoutes(descriptors),
    {
      path: "*",
      element: <NotFoundPage homePath="/" />,
    },
  ];
}

export default buildAdminRoutes;
